/** @format */

import { DiscountPrice } from '@/components/shared/CardRoom/partials/DiscountPricePDP';
import { ContactsBooking } from '@/components/shared/ContactsBooking/ContactsBooking';
import { UILoader, WrapperLoader } from '@/components/ui/Loader';
import { UIText } from '@/components/ui/Text';
import { useLoader } from '@/ducks/bookingNew/hooks/useLoader';
import { TRoom } from '@/ducks/room/declarations';
import { utilityGetRoomMetaTag } from '@/ducks/room/helpers';
import { useAppTranslation } from '@/i18n/declarations';
import { useAppSelector } from '@/redux/hooks';
import { TLocale } from '@/utils/gatsby/types';
import { utilityIsRoomAvailableFrom } from '@/utils/helpers/isRoomAvailableFrom';
import { utilityGetPrice } from '@/utils/helpers/utilityGetPrice';
import { FormattedMessage } from 'gatsby-plugin-intl';
import moment from 'moment';
import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import { selectorBookingAvailability } from '../../../../../@ducks/bookingNew/selectors';
import BookButton from './BookButton';
import { BookCardStartingPriceFrom, CardAvailabilityAvailableFrom, GeneralFrom } from './BookCard.style';
import SuitableForCouples from './SuitableForCouples';

interface BookCardProps {
  room: TRoom;
  locale: TLocale;
}

const DesktopBookCard: React.FC<BookCardProps> = ({ room, locale }) => {
  const availability = useAppSelector(selectorBookingAvailability);
  const { loading: availabilityLoading } = useLoader('availability');
  const { metaTitle } = utilityGetRoomMetaTag(locale, room);
  const isRoomAvailableFrom = utilityIsRoomAvailableFrom(availability);
  const { t } = useAppTranslation();

  const { getDiscountUntilDate, hasPromo, getDiscountedPrice, getBasePrice } = utilityGetPrice({
    availability,
    room,
  });

  const formattedDiscountUntilDate = moment(getDiscountUntilDate()).format('DD/MM/YYYY');
  const isPortugueseCountryCode = room.countryCode === 'PRT';

  const [isClient, setIsClient] = useState(false);

  useEffect(() => {
    setIsClient(true);

    return () => {
      setIsClient(false);
    };
  }, []);

  return (
    <BookCardContainer>
      <BookCardContainerContent>
        <>
          <div>
            {hasPromo() && (
              <DiscountPrice
                oldPrice={getBasePrice()}
                newPrice={getDiscountedPrice()}
                endPromo={formattedDiscountUntilDate}
              />
            )}
            {!hasPromo() && (
              <BookCardStartingPrice>
                <BookCardStartingPriceFrom>
                  <UIText text="from" intlFormatter />{' '}
                </BookCardStartingPriceFrom>
                <span dangerouslySetInnerHTML={{ __html: '&nbsp' }} />
                {room && (
                  <GeneralFrom>
                    <span className="general_from_price">{getBasePrice()}</span>
                    <span className="general_from_price">€</span>
                    <span className="general_from_divider">/</span>
                    <span className="general_from_month">{t('month')}</span>
                  </GeneralFrom>
                )}
              </BookCardStartingPrice>
            )}

            <BookCardMobileTitle>{metaTitle}</BookCardMobileTitle>
            {room.flatFeatures.twoOccupantsAllowed && <SuitableForCouples />}
            <BookCardUtilitiesIncluded>
              <FormattedMessage id="pdp.utilities_included" />
            </BookCardUtilitiesIncluded>
          </div>
          <BookCardLink>
            {availabilityLoading || !isClient ? (
              <WrapperLoader>
                <UILoader />
              </WrapperLoader>
            ) : (
              <>
                <BookButton locale={locale} room={room} />
                {isRoomAvailableFrom && (
                  <CardAvailabilityAvailableFrom>
                    <FormattedMessage id="available_from" /> {moment(availability?.availableFrom).format('DD/MM/YYYY')}
                  </CardAvailabilityAvailableFrom>
                )}
              </>
            )}
          </BookCardLink>
        </>
      </BookCardContainerContent>
      <ContactsBooking room={room} />
    </BookCardContainer>
  );
};

export default DesktopBookCard;

export const BookCardContainer = styled.div`
  background-color: ${({ theme }) => theme.colors.joivy.white};

  position: -webkit-sticky;
  position: sticky;
  top: var(--navigation-bar-offset, 80px);
  z-index: 1;
  width: 100%;
  top: 120px;
  display: inline-block;

  @media screen and (max-width: ${({ theme }) => theme.breakpoints.lg}) {
    display: none;
  }
`;

export const BookCardContainerContent = styled.div`
  padding: 1rem 1.25rem;
  border-radius: 1.875rem;
  border: 1px solid ${({ theme }) => theme.colors.joivy['teal-light']};
`;

export const BookCardStartingPrice = styled.h2`
  display: flex;
  align-items: baseline;
  justify-content: flex-start;
  margin: 0 0 0.5rem;

  font-family: ${({ theme }) => theme.font.body};
  font-size: clamp(1rem, 0.9583rem + 0.1852vw, 1.125rem);
  line-height: clamp(1.375rem, 1.3333rem + 0.1852vw, 1.5rem);
  letter-spacing: clamp(0.03rem, 0.0287rem + 0.0056vw, 0.03375rem);
`;

export const BookCardMobileTitle = styled.h2`
  display: none;
  font-size: 2rem;
  line-height: 1.2;
  letter-spacing: -0.02em;
  margin: 0 0 0.75rem;
`;

export const BookCardLink = styled.div`
  padding-top: 1rem;
`;

export const BookCardUtilitiesIncluded = styled.h3`
  font-size: 0.9375rem;
  margin-block: 0.75rem 0;
  color: ${({ theme }) => theme.colors.joivy['teal-light']};
`;
